/* eslint-disable no-unused-vars */
import { memo, useEffect, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { accountSettingsAPI } from 'services/accountSettings.service';
import ChangePasswordSection from './ChangePasswordSection';
import { Upload, CustomTooltip, CustomInput, CheckboxInner } from 'components';
import { Question } from 'assets/icons';
import { requireRules, emailValidationRules } from './validation';
import { isPublisherRole } from 'store/selectors';
import * as S1 from './Section.styled';

const inputWidth = 285;

export const GeneralInformationSection = memo(() => {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    control,
    getValues,
  } = useFormContext();

  const isPublisher = useSelector(isPublisherRole);

  const getValue = (field) => {
    return watch(`generalInformation.${field}`) || '';
  };

  const handleFileChange = (file) => {
    setValue('generalInformation.profileImage', file, { shouldDirty: true });
    setValue('generalInformation.profileImageUrl', null, { shouldDirty: true });
  };

  const handleChangeFieldValue = (field, value) => {
    setValue(`generalInformation.${field}`, value, { shouldDirty: true });
  };

  return (
    <div>
      <S1.TextBox>
        <S1.AdditionalText>
          Appnext is a monetization platform as well as a community for discussing ideas, creating
          relationship and growing your app business.
          <br /> Fill in more details to get Appnext community to notice you, engage with you,
          promote your business and partner with you.
        </S1.AdditionalText>
        <S1.AdditionalText isBottom>
          Fields marked <S1.Required>*</S1.Required> are required.
        </S1.AdditionalText>
      </S1.TextBox>
      <S1.SectionBox>
        <S1.Row>
          <S1.FieldName>
            Account ID:
            <CustomTooltip
              style={{ marginLeft: '4px' }}
              content={<Question />}
              text={
                <p style={{ maxWidth: '160px' }}>
                  Please provide this account ID as an identifier when contacting Appnext Support.
                </p>
              }
            />
          </S1.FieldName>
          <p style={{ fontSize: '14px', lineHeight: '14px', alignSelf: 'center' }}>
            {getValue('accountId')}
          </p>
        </S1.Row>
        <S1.Row isStart>
          <div>
            <S1.FieldName>Profile Image</S1.FieldName>
            {/* <S1.SubField>
                  Requirements
                  <CustomTooltip
                    style={{ marginLeft: '4px' }}
                    content={<Question />}
                    text={
                      <p style={{ maxWidth: '150px' }}>
                        Appnext is a monetization platform as well as a community for discussing ideas,
                        creating relationship and growing your app business.
                      </p>
                    }
                  />
                </S1.SubField> */}
          </div>
          <div style={{ width: '160px' }}>
            <Controller
              name='generalInformation.profileImageUrl'
              control={control}
              render={({ field: { value } }) => (
                <Upload
                  accept='image/jpeg, image/jpg, image/png, image/gif'
                  value={value}
                  id='file'
                  size={Upload.Size?.Big}
                  onChange={handleFileChange}
                  buttonText='Choose File'
                  environment='accountSettings'
                />
              )}
            />
          </div>
        </S1.Row>
        <S1.Row>
          <S1.FieldName isRequired>First name</S1.FieldName>
          <Controller
            rules={requireRules}
            name='generalInformation.firstName'
            control={control}
            render={({ field, fieldState, formState }) => {
              const { name, onChange, value } = field;
              const { invalid } = fieldState;
              return (
                <CustomInput
                  value={value}
                  autoComplete='off'
                  id={name}
                  name={name}
                  error={invalid}
                  onChange={onChange}
                  width={inputWidth}
                />
              );
            }}
          />
        </S1.Row>
        <S1.Row>
          <S1.FieldName isRequired>Last name</S1.FieldName>
          <Controller
            rules={requireRules}
            name='generalInformation.lastName'
            control={control}
            render={({ field, fieldState, formState }) => {
              const { name, onChange, value } = field;
              const { invalid } = fieldState;
              return (
                <CustomInput
                  value={value}
                  autoComplete='off'
                  id={name}
                  name={name}
                  error={invalid}
                  onChange={onChange}
                  width={inputWidth}
                />
              );
            }}
          />
        </S1.Row>
        <S1.Row>
          <S1.FieldName isRequired>User name</S1.FieldName>
          <Controller
            rules={requireRules}
            name='generalInformation.username'
            control={control}
            render={({ field, fieldState, formState }) => {
              const { name, onChange, value } = field;
              const { invalid } = fieldState;
              return (
                <CustomInput
                  value={value}
                  autoComplete='off'
                  id={name}
                  name={name}
                  error={invalid}
                  onChange={onChange}
                  width={inputWidth}
                />
              );
            }}
          />
        </S1.Row>
        <S1.Row>
          <S1.FieldName isRequired>Email</S1.FieldName>
          <Controller
            rules={emailValidationRules}
            name='generalInformation.email'
            control={control}
            render={({ field, fieldState, formState }) => {
              const { name, onChange, value } = field;
              const { invalid } = fieldState;
              return (
                <CustomInput
                  value={value}
                  autoComplete='off'
                  id={name}
                  name={name}
                  error={invalid}
                  onChange={onChange}
                  width={inputWidth}
                />
              );
            }}
          />
        </S1.Row>
        <S1.Row>
          <S1.FieldName>Password</S1.FieldName>
          <ChangePasswordSection />
        </S1.Row>
        {isPublisher && (
          <S1.Row>
            <S1.FieldName>Notifications</S1.FieldName>
            <CheckboxInner
              name='notifications'
              isChecked={getValue('additionalInfo.getNotifications')}
              id='notifications'
              onChange={(e) => {
                handleChangeFieldValue('additionalInfo.getNotifications', e.target.checked);
              }}
              label='Receive email notification about new direct-buy campaigns opened for your apps'
            />
          </S1.Row>
        )}
      </S1.SectionBox>
    </div>
  );
});

GeneralInformationSection.displayName = 'GeneralInformationSection';
